import { useState, useEffect } from "react";
import api from "../../services/api";
import { Image as ImageIcon, Save, Trash2, Edit, X } from 'lucide-react';
import AdminLayout from '../layouts/AdminLayout';
import PageTitle from '../components/ui/PageTitle';

export default function AdminBannerManager() {
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [banners, setBanners] = useState([]);
    
    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const fetchBanners = async () => {
        try {
            const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
            const response = await api.get('/admin/banners', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.status === 'success') {
                setBanners(response.data.banners);
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!imageFile && !isEditing) {
            alert('Please select a banner image.');
            return;
        }

        const formData = new FormData();
        formData.append('title', title);
        if (subtitle) formData.append('subtitle', subtitle);
        if (linkUrl) formData.append('link_url', linkUrl);
        if (imageFile) formData.append('image', imageFile);
        if (isEditing) formData.append('_method', 'PUT');

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');

        setLoading(true);
        try {
            const url = isEditing ? `/admin/banners/${editId}` : '/admin/banner-upload';
            await api.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            setMessage(isEditing ? 'Hero banner updated successfully!' : 'Hero banner published successfully!');
            setTitle('');
            setSubtitle('');
            setLinkUrl('');
            setImageFile(null);
            setIsEditing(false);
            setEditId(null);
            fetchBanners();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving banner:', error);
            alert('Failed to save banner. Check inputs.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id) => {
        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        try {
            await api.post(`/admin/banners/${id}/toggle-status`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchBanners();
            setMessage('Banner status updated!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error toggling status:', error);
            alert('Failed to change status.');
        }
    };

    const handleEdit = (banner) => {
        setIsEditing(true);
        setEditId(banner.id);
        setTitle(banner.title || '');
        setSubtitle(banner.subtitle || '');
        setLinkUrl(banner.link_url || '');
        setImageFile(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditId(null);
        setTitle('');
        setSubtitle('');
        setLinkUrl('');
        setImageFile(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;

        const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
        try {
            await api.delete(`/admin/banners/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchBanners();
            setMessage('Banner deleted successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting banner:', error);
            alert('Failed to delete banner.');
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-7xl w-full mx-auto space-y-8">
                
                <div className="mb-2">
                    <PageTitle 
                        title="Manage Hero Banner" 
                        subtitle="Add, edit, and manage homepage hero banners and carousel limits." 
                    />
                </div>

                {message && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-medium flex items-center shadow-sm">
                        {message}
                    </div>
                )}

                {/* Upload / Edit Banner Form Box */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <ImageIcon className="text-[#FED547]" size={22} />
                            <h3 className="font-bold text-gray-900 dark:text-white text-base">
                                {isEditing ? 'Edit Hero Banner' : 'Upload New Hero Banner'}
                            </h3>
                        </div>
                        {isEditing && (
                            <button onClick={handleCancelEdit} className="text-xs text-red-500 font-semibold flex items-center gap-1 hover:underline">
                                <X size={16} /> Cancel Edit
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Banner Title</label>
                                <input 
                                    type="text" 
                                    value={title} 
                                    onChange={(e) => setTitle(e.target.value)} 
                                    placeholder="e.g. Find the best job near you"
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Banner Subtitle / Tag</label>
                                <input 
                                    type="text" 
                                    value={subtitle} 
                                    onChange={(e) => setSubtitle(e.target.value)} 
                                    placeholder="e.g. India's No.1 Blue Collar Job Portal"
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">Button / Redirect Link URL (Optional)</label>
                                <input 
                                    type="text" 
                                    value={linkUrl} 
                                    onChange={(e) => setLinkUrl(e.target.value)} 
                                    placeholder="e.g. /job-listing or https://..."
                                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 mb-2">
                                    Banner Background Image {isEditing ? '(Leave blank to keep existing)' : '*'}
                                </label>
                                <input 
                                    type="file" 
                                    accept="image/*"
                                    onChange={(e) => setImageFile(e.target.files[0])} 
                                    required={!isEditing}
                                    className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            {isEditing && (
                                <button 
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl text-sm hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}
                            <button 
                                type="submit" 
                                disabled={loading}
                                className="flex items-center gap-2 bg-[#FED547] text-black px-6 py-2.5 rounded-xl font-bold hover:bg-[#e5be3c] transition-colors shadow-sm text-sm disabled:opacity-70"
                            >
                                <Save size={18} /> {loading ? 'Saving...' : (isEditing ? 'Update Banner' : 'Publish New Banner')}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Banner List Table */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                        <h3 className="font-bold text-gray-900 dark:text-white text-lg">All Banners List (Control Active Carousel Limit)</h3>
                        <span className="text-xs text-gray-500 font-medium">Toggle active to show/hide in 3s auto-scroll carousel</span>
                    </div>

                    {banners.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 text-sm">
                            No banners found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-[#FED547] text-black text-xs uppercase tracking-wider font-bold">
                                    <tr>
                                        <th className="py-4 px-6 w-16">S.NO</th>
                                        <th className="py-4 px-6">PREVIEW</th>
                                        <th className="py-4 px-6">BANNER DETAILS</th>
                                        <th className="py-4 px-6">STATUS (ACTIVE / INACTIVE)</th>
                                        <th className="py-4 px-6 text-center">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                    {banners.map((banner, index) => (
                                        <tr key={banner.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="py-4 px-6 font-semibold">{index + 1}</td>
                                            <td className="py-4 px-6">
                                                <div className="w-24 h-14 bg-gray-900 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                                                    <img src={banner.image_url} alt="Banner" className="w-full h-full object-cover" />
                                                </div>
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="font-bold text-gray-900 text-base">{banner.title || "No Title"}</div>
                                                <div className="text-xs text-gray-500">{banner.subtitle || "No Subtitle"}</div>
                                            </td>
                                            
                                            {/* 🌟 Interactive Toggle Switch Button */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleStatus(banner.id)}
                                                        className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                                                            banner.is_active ? 'bg-green-500' : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-3.5 w-3.5 mt-0.5 ml-0.5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                                                                banner.is_active ? 'translate-x-5' : 'translate-x-0'
                                                            }`}
                                                        />
                                                    </button>
                                                    <span className={`text-xs font-bold ${banner.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {banner.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="py-4 px-6 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button 
                                                        onClick={() => handleEdit(banner)}
                                                        className="p-1.5 text-gray-400 hover:text-yellow-500 rounded"
                                                        title="Edit Banner"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDelete(banner.id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                                                        title="Delete Banner"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </AdminLayout>
    );
}